import {
  Box,
  Button,
  ChakraSelect,
  FileUploader,
  IconButtonWithTooltip,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  useCustomToast,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { mdiUpload } from '@mdi/js';
import Icon from '@mdi/react';
import { useAdaptPriceImportRunApi, useCreatePriceImportRunApi } from 'apis/price-imports';
import { useSearchSuppliersApi } from 'apis/supplier';
import { PRICE_IMPORT_ACCEPT_TYPES, PRICE_IMPORT_MAX_FILE_BYTES } from 'pages/price-imports/utils/import-files';
import { cloneElement, ReactElement, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface SupplierOption {
  value: number;
  label: string;
}

export interface StartImportModalProps {
  /** Optional custom trigger, so the Prices matrix can launch this too. */
  trigger?: ReactElement;
}

/**
 * Upload a supplier's price list and land on its run.
 *
 * Two calls on purpose: POST creates the run (fast, no inference), then
 * POST /:id/adapt does the mapping and matching. Splitting them means no single
 * request holds file I/O, an inference round trip and the matching pass at
 * once, which matters against a 60-second ingress timeout.
 */
export const StartImportModal = ({ trigger }: StartImportModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();
  const toast = useCustomToast();

  const [search, setSearch] = useState('');
  const [supplier, setSupplier] = useState<SupplierOption | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const { data, isLoading: isSearching } = useSearchSuppliersApi(search, { enabled: search.length >= 2 });

  const { mutateAsync: createRun, isLoading: isUploading } = useCreatePriceImportRunApi();
  const { mutateAsync: adaptRun, isLoading: isAdapting } = useAdaptPriceImportRunApi();

  const isBusy = isUploading || isAdapting;

  const options = useMemo<SupplierOption[]>(
    () => (data?.data ?? []).map((entry: { id: number; name: string }) => ({ value: entry.id, label: entry.name })),
    [data]
  );

  const reset = () => {
    setSearch('');
    setSupplier(null);
    setFile(null);
  };

  const handleClose = () => {
    if (isBusy) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!supplier || !file) return;

    try {
      const created = await createRun({ supplierId: supplier.value, file });
      const runId = created.data.id;

      // Adapt failing is not a dead end -- the run exists and the operator can
      // map the columns by hand -- so navigate either way.
      try {
        await adaptRun({ runId });
      } catch {
        toast({
          description: 'The file was uploaded but the columns could not be read automatically. Map them to continue.',
          status: 'error',
        });
      }

      reset();
      onClose();
      history.push(`/app/price-imports/${runId}`);
    } catch {
      // The hook already surfaced the message.
    }
  };

  const triggerElement = trigger ? (
    cloneElement(trigger, { onClick: onOpen })
  ) : (
    <IconButtonWithTooltip
      label='Import a supplier price list'
      aria-label='start-price-import-btn'
      Icon={<Icon path={mdiUpload} size={0.85} />}
      onClick={onOpen}
    />
  );

  return (
    <>
      {triggerElement}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size={{ base: 'full', md: 'lg' }}
        scrollBehavior='inside'
        closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Import a price list</ModalHeader>
          <ModalCloseButton isDisabled={isBusy} />

          <ModalBody>
            <VStack align='stretch' spacing='16px'>
              <Box>
                <Text fontSize='sm' fontWeight='medium' mb='6px'>
                  Supplier
                </Text>
                <ChakraSelect
                  size='sm'
                  options={options}
                  value={supplier}
                  isLoading={isSearching}
                  isDisabled={isBusy}
                  placeholder='Search suppliers…'
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  onInputChange={(input: string) => setSearch(input)}
                  filterOption={() => true}
                  onChange={(option: any) => setSupplier(option ?? null)}
                  noOptionsMessage={() => (search.length < 2 ? 'Type at least 2 characters' : 'No suppliers found')}
                />
              </Box>

              <Box>
                <Text fontSize='sm' fontWeight='medium' mb='6px'>
                  Price list
                </Text>
                <FileUploader
                  style='style_01'
                  message='Drop the supplier price list here or browse — Excel, CSV, or a photo'
                  // MIME-first, and it must include the empty type: see
                  // utils/import-files.ts. An extension list rejects everything.
                  acceptedTypes={PRICE_IMPORT_ACCEPT_TYPES}
                  maxFiles={1}
                  maxFileSize={PRICE_IMPORT_MAX_FILE_BYTES}
                  onUpload={(files) => setFile(files[0] ?? null)}
                  onRemoveAll={() => setFile(null)}
                />
                <Text fontSize='xs' color='gray.500' mt='6px'>
                  PDFs are not supported. Export the list to Excel or CSV, or photograph it. A file imports when its
                  layout is known — the safest start is the Kaso template, downloadable from the run screen.
                </Text>
              </Box>

              {isBusy && (
                <Box>
                  <Progress size='xs' isIndeterminate colorScheme='red' borderRadius='full' />
                  <Text fontSize='xs' color='gray.600' mt='6px'>
                    {isUploading
                      ? 'Uploading the file…'
                      : 'Reading the columns and matching products — this takes a few seconds.'}
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter gap='8px'>
            <Button variant='ghost' size='sm' onClick={handleClose} isDisabled={isBusy}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              size='sm'
              onClick={handleSubmit}
              isLoading={isBusy}
              isDisabled={!supplier || !file}>
              Upload and read
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
